import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ProductAddonsBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    SubscriptionSelector,
    Swatches,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, ProductAddonsConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getPixelsForImage,
    updateBundleConfigWithRealTimeProducts,
    buildMetafieldFromSwatchOptions,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import useCustomLocation from "../../../../hooks/useCustomLocation";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId, MAYUR_TEST_STORE, SPORTS_TOP_URL } from "../../../constants";
import { hideElementsBySelector, showElementsBySelector } from "../../RenderOnStore/utilities";
import { useShopifyProductState } from "../../../../hooks/useShopifyProductState";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { RenderVariantInfo } from "../../BundleBuilder/BundleBuilderBundle/BBProductCard";

function isProductAvailable(product: ProductObj) {
    if (!product || !product.variants) {
        return false; // Return false if product or its variants are undefined/null
    }
    return product?.variants?.some(
        (variant) => (variant.quantityAvailable && variant.quantityAvailable > 0) || variant.availableForSale,
    );
}
const ProductAddonsBundle = ({
    mainProductInfo,
    ...bundleConfig
}: ProductAddonsConfigInterface & {
    mainProductInfo: {
        available: boolean;
        id: string;
        metafields?: string;
        variants: { id: string; available: boolean }[];
    };
}) => {
    return (
        !!bundleConfig && (
            <ProductAddonsPreview
                bundleConfig={bundleConfig}
                standAlone={true}
                mainProductInfo={mainProductInfo}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_ProductAddonsPreview = {
    bundleConfig: ProductAddonsConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
    SCREEN?: string;
    mainProductInfo?: {
        id: string;
        available: boolean;
        metafields?: any;
        variants?: {
            id: string;
            available: boolean;
        }[];
    };
};

const ProductCardShimmer = ({
    brandingVisible,
    hasRequiredProduct = true,
}: {
    brandingVisible: boolean;
    hasRequiredProduct?: boolean;
}) => {
    const text = hasRequiredProduct ? "Select Add-on product(s)" : "Select Specific product(s)";
    return (
        <div className={styles.shimmerContainer}>
            <span className={styles.shimmerText}>{text}</span>
        </div>
    );
};

const ProductAddonsCarousel = ({
    products,
    renderProduct,
    title,
}: {
    products: ProductObj[];
    renderProduct: (product: ProductObj, index: number) => ReactNode;
    title?: ReactNode;
}) => {
    const requiredProducts = products?.filter((product) => product.required_product);
    const slides = products?.filter((product) => !product.required_product);

    const [activeProduct, setActiveProduct] = useState(0);

    useEffect(() => {
        setActiveProduct((currentIndex) => (slides.length ? currentIndex % slides.length : 0));
    }, [slides.length]);

    if (!products.length) return null;

    return (
        <div className={styles.carousel}>
            {requiredProducts.map((product, index) => (
                <div key={product.id} hidden>
                    {renderProduct(product, index)}
                </div>
            ))}
            {(title || slides.length > 1) && (
                <div className={styles.carouselHeader}>
                    {title}
                    {slides.length > 1 && (
                        <div className={styles.carouselControls}>
                            <button
                                type="button"
                                className={styles.carouselButton}
                                onClick={() => setActiveProduct((index) => (index - 1 + slides.length) % slides.length)}
                                aria-label="Show previous add-on product"
                            >
                                <MdChevronLeft aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={styles.carouselButton}
                                onClick={() => setActiveProduct((index) => (index + 1) % slides.length)}
                                aria-label="Show next add-on product"
                            >
                                <MdChevronRight aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </div>
            )}
            <div className={styles.carouselProduct}>
                {slides.map((product, index) => (
                    <div key={product.id} hidden={index !== activeProduct}>
                        {renderProduct(product, index)}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AiProductCards = ({
    bundleConfig,
    appConfigContext,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
    SCREEN,
    title,
}: {
    bundleConfig: any;
    appConfigContext: TStoreContext;
    finalProductsList: any;
    setFinalProductsList: any;
    shouldDiscountApply: boolean;
    SCREEN?: string;
    title?: ReactNode;
}) => {
    const { sft } = appConfigContext;
    const getAiFbtData = async (_sft) => {
        try {
            const offered_products = bundleConfig.offered_products;
            bundleConfig.offered_products = []; // we are making it empty just to amke sure,that we dont get the offered_prducts array item in products.
            const initialData = await updateBundleConfigWithRealTimeProducts(_sft, [bundleConfig], false, 1);
            bundleConfig.offered_products = offered_products;

            if (initialData?.length > 0 && initialData[0]?.products?.length) {
                const mainProducts = initialData[0]?.products || [];
                const offeredProducts = bundleConfig?.hide_out_of_stock_products
                    ? initialData[0]?.offered_products?.filter((p: any) => isProductAvailable(p))
                    : initialData[0]?.offered_products || [];

                if (mainProducts.length > 0) {
                    const finalProducts = [...mainProducts, ...offeredProducts].map((product, i) => ({
                        ...product,
                        checked: bundleConfig?.allow_radio_selection
                            ? i == 1
                                ? true
                                : false
                            : bundleConfig?.no_preselect_offered_products
                              ? false
                              : isProductAvailable(product),
                        required_product: mainProducts.includes(product),
                        quantity: product?.quantity || 1,
                    }));

                    setFinalProductsList(finalProducts);
                    return;
                }
            }
        } catch (error) {
            console.error("Error fetching AI FBT data:", error);
        }
    };

    useEffect(() => {
        if (SCREEN == "GLOABL_SETTINGS") {
            return;
        }
        getAiFbtData(sft);
        // setFinalProductsList(bundleConfig?.offered_products);
    }, [
        bundleConfig?.product_add_on_selection,
        bundleConfig?.offered_products?.length,
        bundleConfig?.no_preselect_offered_products,
    ]);

    const aiAddons = finalProductsList.filter((product: any) => product.required_product === false);
    const renderAiProduct = (product: any, index: number) => (
        <ProductCard
            key={product?.id}
            index={index}
            product={product}
            bundleConfig={bundleConfig}
            appConfigContext={appConfigContext}
            standAlone={false}
            setTrackVariant={() => {}}
            isMobile={false}
            isCarousel={bundleConfig?.bundle_layout === "carousel"}
            setFinalProductsList={setFinalProductsList}
            finalProductsList={finalProductsList}
            isAiFbt={true}
            shouldDiscountApply={shouldDiscountApply}
            variants={product?.variants}
        />
    );

    if (bundleConfig?.offered_products?.length <= 0) {
        return <ProductCardShimmer brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi} />;
    }

    return bundleConfig?.bundle_layout === "carousel" ? (
        <ProductAddonsCarousel products={aiAddons} renderProduct={renderAiProduct} title={title} />
    ) : (
        <div className={styles.products} style={{ flexWrap: "nowrap" }}>
            {aiAddons.map(renderAiProduct)}
        </div>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTrackVariant = () => {},
    isMobile,
    finalProductsList,
    setFinalProductsList,
    isAiFbt = false,
    isCarousel = false,
    shouldDiscountApply,
    variants,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: ProductAddonsConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTrackVariant: any;
    variants: VariantsObj[];
    isMobile: boolean;
    setFinalProductsList: any;
    finalProductsList: any[] | [];
    isAiFbt?: boolean;
    isCarousel?: boolean;
    shouldDiscountApply: boolean;
}) => {
    const [disPrice, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const { variantId, quantity } = useShopifyProductState(standAlone && product.required_product);
    const { currency, money_format } = appConfigContext;
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>([]);
    const [showVariantName, setShowVariantName] = useState("");

    useEffect(() => {
        if (
            product?.variants?.length == 1 &&
            product.variants[0].title?.toLowerCase() != product?.title?.toLowerCase()
        ) {
            setShowVariantName(product.variants[0].title || "");
        } else {
            setShowVariantName("");
        }
    }, [product]);

    useEffect(() => {
        if (product?.required_product) {
            setSelectedVariants(
                new Array(quantity)
                    ?.fill(product?.variants?.find((vv) => vv.id.split("/").pop() == variantId))
                    ?.map((variant) => ({
                        ...variant,
                        selling_plan: !product.oneTimePurchase
                            ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                            : "",
                        copyPrice: variant?.price,
                    })),
            );
        }
    }, [variantId, quantity]);

    useEffect(() => {
        if (!product?.variants) return;

        setSelectedVariants((prev) => {
            const currentQuantity = product?.quantity || 1;
            if (prev.length === currentQuantity) return prev;

            if (prev.length < currentQuantity) {
                // Add more selections
                const defaultVariant =
                    product?.variants?.find((vv) => vv.availableForSale == true) || product?.variants?.[0];
                const additions = new Array(currentQuantity - prev.length).fill(null).map(() => ({
                    ...defaultVariant,
                    selling_plan: !product.oneTimePurchase
                        ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                        : "",
                    copyPrice: defaultVariant?.price,
                }));
                return [...prev, ...additions];
            } else {
                // Reduce selections
                return prev.slice(0, currentQuantity);
            }
        });
    }, [product?.quantity, product?.variants]);

    useEffect(() => {
        if (product?.variants?.length) {
            let disP = +product.variants[0].price * product?.quantity;
            const origP = +product.variants[0].price * product?.quantity;

            // Apply discount only if the product is NOT a required product
            if (!product.required_product) {
                // Check if individual discounts are being used
                if (
                    bundleConfig?.apply_discount_on_individual_products === true &&
                    bundleConfig?.individual_product_discounts?.[product.id]
                ) {
                    const individualDiscount = bundleConfig.individual_product_discounts[product.id];
                    const dis = individualDiscount.discount_value || 0;

                    switch (bundleConfig?.discount_type) {
                        case "percentage":
                            disP = disP - disP * (dis / 100);
                            break;
                        case "flat":
                            disP = disP - dis;
                            break;
                        default:
                    }
                } else {
                    const dis = bundleConfig?.discount_value || 0;
                    switch (bundleConfig?.discount_type) {
                        case "percentage":
                            disP = disP - disP * (dis / 100);
                            break;
                        case "flat":
                            disP = disP - dis;
                            break;
                        default:
                    }
                }
            }

            console._log("fbt ", disP);

            setDisPrice(Math.round(disP * 100) / 100);
            setOrigPrice(Math.round(origP * 100) / 100);
        } else {
            setDisPrice(0);
            setOrigPrice(0);
        }
    }, [
        product,
        bundleConfig?.discount_type,
        bundleConfig?.discount_value,
        bundleConfig?.apply_discount_on_individual_products,
        bundleConfig?.individual_product_discounts,
    ]);

    useEffect(() => {
        // if (product.checked) {
        // Add productIdx to each selectedVariant
        const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
            ...variant,
            productIdx: index, // Add the product index
        }));
        product.selectedVariants = updatedSelectedVariants; // Assign updated variants to the product

        // Update trackVariant state
        setTrackVariant((prevVariants: any[]) => {
            const updatedVariants = [...prevVariants];
            // Check if product ID already exists in the array
            const productExists = prevVariants.some((variant) => variant?.id === updatedSelectedVariants.id);

            if (!productExists) {
                updatedVariants[index] = updatedSelectedVariants; // Update only if product doesn't exist
            }
            return updatedVariants;
        });

        let totalOriginalPrice = 0;
        let totalDiscountedPrice = 0;

        // Calculate total original price
        updatedSelectedVariants.forEach((variant: any) => {
            // if (product.checked) {
            const variantPrice = +variant?.price;
            totalOriginalPrice += variantPrice;
            // }
        });
        // Apply discount based on bundleConfig after summing the prices
        totalDiscountedPrice = totalOriginalPrice;
        const discountValue =
            bundleConfig?.apply_discount_on_individual_products === true &&
            bundleConfig?.individual_product_discounts?.[product.id]
                ? bundleConfig.individual_product_discounts[product.id].discount_value
                : bundleConfig?.discount_value || 0;

        switch (bundleConfig?.discount_type) {
            case "percentage":
                totalDiscountedPrice = totalOriginalPrice - totalOriginalPrice * (discountValue / 100);
                break;
            case "flat":
                totalDiscountedPrice = totalOriginalPrice - discountValue;
                break;
            default:
                break;
        }

        // Set the final calculated prices, rounded to 2 decimal places
        setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
        setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
        // }
    }, [selectedVariants, product, bundleConfig, index]);

    const handleProductClick = (e: any) => {
        e?.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
        }
    };

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const isProductAvailable = useMemo(() => {
        return product?.variants?.some(
            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [product]);

    // Unit price support: divide by qty when show_unit_price is on
    const divisor = bundleConfig?.show_unit_price ? Math.max(selectedVariants.length, 1) : 1;
    const displayPrice = (product.required_product ? origPrice : disPrice) / divisor;
    const displayComparePrice =
        (bundleConfig?.use_compare_at_price
            ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
            : origPrice) / divisor;
    const showComparePrice = !product.required_product && displayComparePrice > displayPrice;

    const formatPrice = (price: number) =>
        price <= 0 ? bundleConfig?.free_text || "Free" : getPriceLabel(price, money_format, currency);

    const getDiscountLabel = () => {
        if (product.required_product) {
            return null;
        }

        const discountType = bundleConfig?.discount_type;
        const discountValue =
            bundleConfig?.apply_discount_on_individual_products === true &&
            bundleConfig?.individual_product_discounts?.[product.id]
                ? bundleConfig.individual_product_discounts[product.id].discount_value || 0
                : bundleConfig?.discount_value || 0;

        if (!discountType || discountType === "none" || discountValue === 0) {
            return null;
        }

        const formattedFlatDiscount = getPriceLabel(discountValue, money_format, currency, true, "string");

        switch (discountType) {
            case "percentage":
                return bundleConfig?.option_off_text
                    ? bundleConfig.option_off_text.replace("{discount}", `${discountValue}%`)
                    : `${discountValue}% off`;

            case "flat":
                return bundleConfig?.option_off_text
                    ? bundleConfig.option_off_text.replace("{discount}", `${formattedFlatDiscount}`)
                    : `${formattedFlatDiscount} off`;

            default:
                return null;
        }
    };

    const renderVariantInformation = () => {
        // @ts-expect-error hack
        if (product?.variantsCount?.count > 1 && product.variants.length == 1) {
            return (
                <RenderVariantInfo
                    product={showVariantName ? { ...product, title: showVariantName } : product}
                    style={{
                        color: bundleConfig?.bundles_design?.variant_selector_text_color,
                        fontSize: bundleConfig?.bundles_design?.variant_selector_text_size,
                        fontWeight: bundleConfig?.bundles_design?.variant_selector_text_style?.split(" ")[0] ?? "450",
                        fontStyle: bundleConfig?.bundles_design?.variant_selector_text_style?.split(" ")[1] ?? "normal",
                    }}
                    bundleType={bundleConfig?.bundle_type}
                />
            );
        }
    };

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const renderPrice = (
        <div
            className={styles.priceContainer}
            style={{
                ...(isMobile && {
                    width: "100%",
                }),
            }}
        >
            <div
                className={styles.priceLabelWrapper}
                style={{
                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                    ...(isMobile && {
                        flexWrap: "wrap",
                        maxWidth: "80%",
                    }),
                }}
            >
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                    <div style={{ display: "flex", alignItems: "baseline" }}>
                        <span>{formatPrice(displayPrice)}</span>
                        {bundleConfig?.show_unit_price && bundleConfig?.show_unit_price_text && (
                            <span
                                style={{
                                    fontSize: (bundleConfig?.bundles_design.highlighted_price_tag_size as number) - 2,
                                }}
                            >
                                {bundleConfig.show_unit_price_text}
                            </span>
                        )}
                    </div>
                </InspectorEditableBlock>

                {showComparePrice && (
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                        <div
                            className={styles.compareAtPriceLayout}
                            style={{
                                fontSize: bundleConfig?.bundles_design.product_price_size,
                                color: bundleConfig?.bundles_design.product_price_color,
                                fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                            }}
                        >
                            {formatPrice(displayComparePrice)}
                        </div>
                    </InspectorEditableBlock>
                )}
            </div>
            <span
                className={styles.discountLabelTag}
                style={{ ...(isMobile && { textAlign: "start", maxWidth: "80%" }) }}
            >
                {getDiscountLabel()}
            </span>
        </div>
    );

    const renderVariantSelectors =
        product.variants.length > 1 ? (
            <div className={styles.variantSelectorsWrapper}>
                {selectedVariants.map((_: any, i: number) =>
                    userShopname === "typsy-beauty.myshopify.com" ? (
                        <Swatches
                            bundlesDesign={bundleConfig?.bundles_design}
                            key={i}
                            variants={product.variants}
                            handleChange={setSelectedVariants}
                            selected={selectedVariants}
                            options={product.options || []}
                            productCardIndex={i}
                            standAlone={standAlone}
                        />
                    ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                        <CustomSwatches
                            key={i}
                            variants={product.variants}
                            handleChange={setSelectedVariants}
                            selected={selectedVariants}
                            options={product.options || []}
                            productCardIndex={i}
                            standAlone={standAlone}
                            metafield={effectiveMetafield}
                            bundlesDesign={bundleConfig?.bundles_design}
                            bundleConfig={bundleConfig}
                        />
                    ) : (
                        <VariantSelectorV2
                            key={i}
                            variants={product.variants}
                            handleChange={setSelectedVariants}
                            selected={selectedVariants}
                            options={product.options || []}
                            productCardIndex={i}
                            standAlone={standAlone}
                            metafield={effectiveMetafield}
                            bundlesDesign={bundleConfig?.bundles_design}
                            bundleConfig={bundleConfig}
                        />
                    ),
                )}
            </div>
        ) : null;

    return (
        <>
            {!product?.required_product && (
                <InspectorEditableBlock
                    bundleType={bundleConfig?.bundle_type}
                    settingKey="productCard"
                    tagsKey={product?.tags?.toString()}
                    productId={getProductId(product?.id)}
                >
                    <div
                        className={`${styles.productWrapper} ${isCarousel ? styles.carouselCard : ""}`}
                        style={{
                            border: `${product.checked ? 2 : 1}px solid ${
                                bundleConfig?.bundles_design?.product_border_color
                            }`,
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            backgroundColor: bundleConfig?.bundles_design.product_background_color,
                        }}
                        onClick={(e) => {
                            if (bundleConfig?.allow_radio_selection) {
                                setFinalProductsList((ps: ProductObj[]) => {
                                    return ps.map((fpl: any) => ({
                                        ...fpl,
                                        checked:
                                            fpl.id == window?.kefi_bundles?.product?.id ? true : fpl.id == product.id, // Set the selected radio button to checked
                                    }));
                                });
                            }
                        }}
                    >
                        {/* {index > 0 && (
                    <div
                        className={styles.plusSign}
                        style={{
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            width: "10px",
                            transform: "scale(2)",
                        }}
                    >
                        <PlusIcon />
                    </div>
                )} */}
                        <div className={styles.productInfoRight}>
                            {bundleConfig?.fbt_show_checkboxes == 1 && (
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productCheckbox"
                                >
                                    {bundleConfig?.allow_radio_selection ? (
                                        <input
                                            id={styles.fbtInputCheckbox}
                                            style={{
                                                accentColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                                cursor: "pointer",
                                            }}
                                            type="radio"
                                            name="bundle-products"
                                            disabled={!isProductAvailable}
                                            key={product.id}
                                            value={product.id}
                                            onChange={(e) => {
                                                const currentVal = e.target.value;
                                                setFinalProductsList((ps: ProductObj[]) => {
                                                    return ps.map((fpl: any) => ({
                                                        ...fpl,
                                                        checked:
                                                            fpl.id == window?.kefi_bundles?.product?.id
                                                                ? true
                                                                : fpl.id == currentVal, // Set the selected radio button to checked
                                                    }));
                                                });
                                            }}
                                            checked={
                                                !!finalProductsList?.find((fpl: ProductObj) => fpl?.id == product?.id)
                                                    ?.checked
                                            }
                                        />
                                    ) : (
                                        <input
                                            id={styles.fbtInputCheckbox}
                                            style={{
                                                accentColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                            }}
                                            type={"checkbox"}
                                            disabled={!isProductAvailable}
                                            key={product.id}
                                            name={product.title}
                                            value={product.id}
                                            onChange={(e) => {
                                                console._log(e.target.value);
                                                const currentCheck = e.currentTarget.checked;
                                                const currentVal = e.target.value;
                                                setFinalProductsList((ps: ProductObj[]) => {
                                                    return ps.map((fpl: any) => {
                                                        if (fpl.id == currentVal) {
                                                            return {
                                                                ...fpl,
                                                                checked: currentCheck,
                                                            };
                                                        } else {
                                                            return fpl;
                                                        }
                                                    });
                                                });
                                            }}
                                            checked={
                                                !!finalProductsList?.find((fpl: ProductObj) => fpl?.id == product?.id)
                                                    ?.checked
                                            }
                                        />
                                    )}
                                </InspectorEditableBlock>
                            )}
                            <ProductImageWrapper
                                product={product}
                                handleProductClick={handleProductClick}
                                bundleConfig={{
                                    ...bundleConfig,
                                    bundles_design: {
                                        ...bundleConfig?.bundles_design,
                                        product_img_size: bundleConfig?.bundles_design.product_img_size
                                            ? bundleConfig?.bundles_design.product_img_size
                                            : "small",
                                    },
                                }}
                                isMobile={isMobile}
                                selectedVariants={selectedVariants}
                                overrideQtyVisibility={true}
                            />
                            <div
                                className={styles.productMetaRight}
                                style={{
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    gap: isMobile
                                        ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") /
                                              10 +
                                          "px"
                                        : "0px",
                                    minHeight: +getPixelsForImage(
                                        bundleConfig?.bundles_design?.product_img_size,
                                        "number",
                                    ),
                                    whiteSpace: "normal" /* Allows text to wrap onto the next line */,
                                    ...(isMobile && {
                                        flexDirection: "column",
                                    }),
                                }}
                            >
                                <div
                                    className={styles.productTitleWrapper}
                                    style={{
                                        fontSize: bundleConfig?.bundles_design.product_name_size,
                                        color: bundleConfig?.bundles_design?.product_name_color,
                                        fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                        ...(bundleConfig?.allow_radio_selection && {
                                            cursor: "pointer",
                                        }),
                                        ...(isMobile && {
                                            textAlign: "start",
                                        }),
                                    }}
                                >
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productTitle"
                                        wrapWithDiv={true}
                                    >
                                        <div
                                            className={styles.productTitleLayout}
                                            style={{
                                                justifyContent: isMobile ? "space-between" : "flex-start",
                                                gap: isMobile ? 4 : 8,
                                                ...(isMobile && { marginRight: "0px" }),
                                            }}
                                            onClick={handleProductClick}
                                        >
                                            {product?.title}
                                            {/* {(!isMobile ||
                                    !updateShowImage(
                                        bundleConfig?.bundles_design
                                            ?.product_img_show,
                                        isMobile,
                                    )) && (
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div className={styles.qtyValue}>
                                            {`Qty : ${product.quantity || 0}`}
                                        </div>

                                        <ProductSubscriptionsPreview
                                            product={product}
                                            setSelectedVariants={
                                                setSelectedVariants
                                            }
                                            key={product.id}
                                            bundleConfig={bundleConfig}
                                        />
                                    </div>
                                )} */}
                                        </div>
                                    </InspectorEditableBlock>
                                    {renderVariantInformation()}
                                    {!isMobile && !isCarousel && (
                                        <>
                                            {isProductAvailable ? (
                                                renderPrice
                                            ) : (
                                                <span className={styles.outOfStock}>
                                                    {bundleConfig?.no_stock_text || "Out of stock"}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>

                                {isCarousel && renderVariantSelectors}
                                <div className={styles.subInfo}>
                                    {(isMobile || isCarousel) &&
                                        (isProductAvailable ? (
                                            renderPrice
                                        ) : (
                                            <span className={styles.outOfStock}>
                                                {bundleConfig?.no_stock_text || "Out of stock"}
                                            </span>
                                        ))}
                                </div>
                            </div>
                        </div>
                        {!isCarousel && renderVariantSelectors}

                        {product.isSubscribed && (
                            <div className={styles.subscriptionSelectorWrapper}>
                                <div className={styles.separatorWrapper}>
                                    <span className={styles.separator}></span>
                                </div>

                                <SubscriptionSelector
                                    product={product}
                                    selectedVariants={selectedVariants}
                                    setSelectedVariants={setSelectedVariants}
                                />
                            </div>
                        )}
                    </div>
                </InspectorEditableBlock>
            )}
        </>
    );
};

export const ProductAddonsPreview = ({
    bundleConfig,
    standAlone,
    appConfigContext,
    SCREEN = "",
    mainProductInfo,
}: Props_ProductAddonsPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const { currency, money_format } = appConfigContext;
    const [trackVariant, setTrackVariant] = useState<any[]>([]);
    const bundleRef = useRef(null);
    const { isMobile } = useIsMobile({ elementRef: bundleRef });
    const [shouldDiscountApply, setShouldDiscountApply] = useState(true);
    const [finalProductsList, setFinalProductsList] = useState<ProductObj[]>([]);
    const [isAddToCartDisabled, setIsAddToCartDisabled] = useState(false);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;

    // Note: do not remove this, it runs and handles shop's variant selection
    const currentPageLocation = useCustomLocation();
    console._log("finalProductsList", finalProductsList, currentPageLocation);

    const currentVariantId = useMemo(() => {
        const variantIdMatch = window?.ShopifyAnalytics?.meta?.selectedVariantId;
        return variantIdMatch || getProductId(mainProductInfo?.variants?.[0]?.id || "");
    }, [currentPageLocation, mainProductInfo]);

    const configuredOfferedProducts = useMemo(() => {
        let addons = [];
        if (bundleConfig?.same_addon_for_all_variants) {
            addons = bundleConfig?.offered_products || [];
        } else {
            // Variant-wise logic
            let addonsTemp = currentVariantId
                ? bundleConfig?.variant_addons?.[currentVariantId] ||
                  bundleConfig?.variant_addons?.[`gid://shopify/ProductVariant/${currentVariantId}`]
                : undefined;

            const isVariantSelectedInUrl = currentPageLocation?.search?.includes("variant=");

            if (!isVariantSelectedInUrl && (!addonsTemp || addonsTemp.length === 0) && bundleConfig?.variant_addons) {
                const firstVariantWithAddons = Object.values(bundleConfig.variant_addons).find(
                    (vAddons: any) => vAddons && vAddons.length > 0,
                );
                if (firstVariantWithAddons) {
                    addonsTemp = firstVariantWithAddons as any;
                }
            }
            addons = addonsTemp || [];
        }
        return addons;
    }, [bundleConfig, currentVariantId, currentPageLocation]);

    const activeOfferedProducts = useMemo(() => {
        if (bundleConfig?.hide_out_of_stock_products) {
            return configuredOfferedProducts?.filter((p: any) => isProductAvailable(p)) || [];
        }
        return configuredOfferedProducts;
    }, [configuredOfferedProducts, bundleConfig?.hide_out_of_stock_products]);

    useEffect(() => {
        if (!standAlone) return;

        const isLegacy = bundleConfig?.same_addon_for_all_variants;
        const hasAddons = configuredOfferedProducts.length > 0;
        const selectors = bundleConfig?.hide_native_elements ?? "";

        if (isLegacy || hasAddons) {
            hideElementsBySelector(selectors);
        } else {
            showElementsBySelector(selectors);
        }
    }, [
        bundleConfig?.same_addon_for_all_variants,
        configuredOfferedProducts,
        bundleConfig?.hide_native_elements,
        standAlone,
    ]);

    useEffect(() => {
        if (userShopname === SPORTS_TOP_URL || userShopname === MAYUR_TEST_STORE) {
            if (currentPageLocation?.search?.includes("variant")) {
                setIsAddToCartDisabled(false);
            } else if (window?.kefi_bundles?.product?.variants?.length == 1) {
                setIsAddToCartDisabled(false);
            } else {
                setIsAddToCartDisabled(true);
            }
        }
    }, [currentPageLocation]);

    useEffect(() => {
        if (finalProductsList) {
            let totalPrice = 0;
            let compPrice = 0;
            // Loop through all selected variants from each product in trackVariant
            trackVariant.forEach((productVariants) => {
                if (productVariants && productVariants.length) {
                    productVariants.forEach((variant: any) => {
                        if (variant && variant.price) {
                            totalPrice += +variant.price; // Add each variant's price to the total
                        }
                        if (variant && variant.compareAtPrice) {
                            compPrice += +variant.compareAtPrice;
                        }
                    });
                }
            });

            // Set the final total price
            setTotalPrice(totalPrice);
            setCompareAtPrice(compPrice);
        }
    }, [trackVariant, finalProductsList, bundleConfig?.discount_type, bundleConfig?.discount_value]);

    useEffect(() => {
        const temp: any[] = [];
        let isRequiredChecked = false;
        let isNonRequiredChecked = false;
        finalProductsList?.forEach((pd, idx: number) => {
            if (idx === 0 && !standAlone) {
                window.localStorage.setItem("addons_handle", pd.handle);
            }
            if (pd.required_product && pd.checked) {
                isRequiredChecked = true;
            }
            if (!pd.required_product && pd.checked) {
                isNonRequiredChecked = true;
            }
            if (pd.checked) {
                temp.push(pd.selectedVariants);
            }
        });
        setShouldDiscountApply(isNonRequiredChecked);
        setTrackVariant(temp);
    }, [finalProductsList, standAlone]);

    useEffect(() => {
        if (
            (bundleConfig?.product_add_on_selection !== "all" && (bundleConfig?.products?.length || 0) > 0) ||
            (activeOfferedProducts?.length || 0) > 0
        ) {
            const arr = [
                ...(bundleConfig?.products?.slice(0, 1)?.map((obj) => ({
                    ...obj,
                    checked: isProductAvailable(obj),
                    required_product: true,
                    quantity: obj?.quantity || 1,
                    selectedVariants: obj.selectedVariants || [
                        {
                            id: currentVariantId || obj?.variants?.[0]?.id || obj?.id,
                            quantity: obj?.quantity || 1,
                        },
                    ],
                })) || []),
                ...(activeOfferedProducts?.map((obj, i) => ({
                    ...obj,
                    checked: bundleConfig?.allow_radio_selection
                        ? i == 0
                            ? true
                            : false
                        : bundleConfig?.no_preselect_offered_products
                          ? false
                          : isProductAvailable(obj),
                    required_product: false,
                    quantity: obj?.quantity || 1,
                })) || []),
            ];
            setFinalProductsList(arr);
        } else if (
            bundleConfig?.product_add_on_selection == "manual" ||
            bundleConfig?.product_add_on_selection == "collection"
        ) {
            setFinalProductsList([]);
        }
    }, [
        bundleConfig.products,
        activeOfferedProducts,
        bundleConfig?.product_add_on_selection,
        bundleConfig?.no_preselect_offered_products,
        bundleConfig?.allow_radio_selection,
    ]);

    const displayedProducts: ProductObj[] = finalProductsList.filter((product) => !product.required_product);
    const isCarouselLayout = bundleConfig?.bundle_layout === "carousel";
    const bundleTitle = (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="bundleTitle">
            <div
                className={styles.addonsTitle}
                style={{
                    color: bundleConfig?.bundles_design?.bundle_title_color,
                    fontSize: bundleConfig?.bundles_design?.bundle_title_size,
                    fontWeight: bundleConfig?.bundles_design?.bundle_title_style?.split(" ")[0],
                    fontStyle: bundleConfig?.bundles_design?.bundle_title_style?.split(" ")[1],
                    marginBottom: isCarouselLayout ? 0 : 10,
                    textAlign: isCarouselLayout ? "left" : "center",
                }}
            >
                {bundleConfig?.title}
            </div>
        </InspectorEditableBlock>
    );

    if (!bundleConfig) return null;

    const shouldShowAddonSection = bundleConfig?.hide_out_of_stock_products
        ? activeOfferedProducts.length > 0
        : bundleConfig?.same_addon_for_all_variants || activeOfferedProducts.length > 0;

    const updatedBundleConfig = {
        ...bundleConfig,
        title: shouldShowAddonSection ? bundleConfig.title : "",
        products: finalProductsList.filter((fpl) => !!fpl.checked),
        discount_type: shouldDiscountApply ? bundleConfig?.discount_type : "none",
        discount_value: shouldDiscountApply ? bundleConfig?.discount_value : "0",
        ...((userShopname === SPORTS_TOP_URL || userShopname === MAYUR_TEST_STORE) && {
            disableAddToCartButton: isAddToCartDisabled,
        }),
    };

    const isMainProductOutOfStock = () => {
        if (!mainProductInfo?.variants?.length || !currentPageLocation?.search) return false;

        // Extract variant ID from search string
        const variantIdMatch = currentPageLocation.search.match(/variant=(\d+)/);
        const currentVariantId = variantIdMatch?.[1];

        if (!currentVariantId) return false;

        // Find matching variant in product.variants
        const matchedVariant = mainProductInfo.variants.find((variant: any) => String(variant.id) === currentVariantId);

        return matchedVariant ? !matchedVariant.available : false; //  true if out of stock
    };

    return (
        <div
            className={styles.previewMainContainer}
            style={{
                zoom: standAlone ? 1 : 0.8,
                overflow: standAlone ? "hidden" : "",
                justifyContent: standAlone ? "flex-start" : "center",
            }}
        >
            <BundlePreviewWrapper
                bundleConfig={updatedBundleConfig as any}
                standAlone={standAlone}
                currency={currency}
                money_format={money_format}
                totalPrice={totalPrice}
                compareAtPriceTotal={compareAtPrice}
                isMainProductOutOfStock={isMainProductOutOfStock()}
                hideBundleTitle={true}
            >
                {!isCarouselLayout && shouldShowAddonSection && bundleTitle}
                {!standAlone &&
                    activeOfferedProducts?.length == 0 &&
                    bundleConfig?.offered_products?.length > 0 &&
                    bundleConfig?.hide_out_of_stock_products && (
                        <div
                            style={{
                                padding: "10px",
                                borderRadius: "4px",
                                color: "#cc0000",
                                marginBottom: "10px",
                                textAlign: "center",
                                fontSize: "14px",
                                fontWeight: "500",
                            }}
                        >
                            All selected add-on products are currently out of stock. The bundle will be hidden on the
                            storefront if "Hide Out of Stock Products" is enabled.
                        </div>
                    )}
                {!shouldShowAddonSection &&
                    finalProductsList
                        ?.filter((product) => product.required_product)
                        ?.map((product) => (
                            <div key={product.id} hidden>
                                <ProductCard
                                    index={finalProductsList.indexOf(product)}
                                    product={product}
                                    bundleConfig={bundleConfig}
                                    appConfigContext={appConfigContext}
                                    standAlone={!!standAlone}
                                    setTrackVariant={setTrackVariant}
                                    isMobile={isMobile}
                                    setFinalProductsList={setFinalProductsList}
                                    finalProductsList={finalProductsList}
                                    shouldDiscountApply={shouldDiscountApply}
                                    variants={product?.variants}
                                />
                            </div>
                        ))}
                {!shouldShowAddonSection ? null : (
                    <>
                        {!standAlone && bundleConfig?.product_add_on_selection == "all" ? (
                            <>
                                <AiProductCards
                                    bundleConfig={{
                                        ...bundleConfig,
                                        form_id: bundleConfig?.form_id || "123",
                                    }}
                                    appConfigContext={appConfigContext}
                                    finalProductsList={finalProductsList}
                                    shouldDiscountApply={shouldDiscountApply}
                                    setFinalProductsList={setFinalProductsList}
                                    SCREEN={SCREEN}
                                    title={bundleTitle}
                                />
                            </>
                        ) : finalProductsList?.length ? (
                            <div
                                className={`${styles.products} ${standAlone && styles.productsOverflow}`}
                                style={{ paddingBottom: userShopname === SPORTS_TOP_URL ? 0 : 10 }}
                                ref={bundleRef}
                            >
                                {displayedProducts.length > 0 ? (
                                    isCarouselLayout ? (
                                        <ProductAddonsCarousel
                                            products={finalProductsList}
                                            title={bundleTitle}
                                            renderProduct={(product) => {
                                                const index = finalProductsList.indexOf(product);

                                                return (
                                                    <ProductCard
                                                        key={
                                                            (bundleConfig.same_addon_for_all_variants
                                                                ? ""
                                                                : currentVariantId + "-") +
                                                            product?.id +
                                                            product?.variants?.[0]?.id
                                                        }
                                                        index={index}
                                                        product={product}
                                                        bundleConfig={bundleConfig}
                                                        appConfigContext={appConfigContext}
                                                        standAlone={!!standAlone}
                                                        setTrackVariant={setTrackVariant}
                                                        isMobile={isMobile}
                                                        isCarousel={true}
                                                        setFinalProductsList={setFinalProductsList}
                                                        finalProductsList={finalProductsList}
                                                        shouldDiscountApply={shouldDiscountApply}
                                                        variants={product?.variants}
                                                    />
                                                );
                                            }}
                                        />
                                    ) : (
                                        finalProductsList?.map((product, index) => {
                                            return (
                                                <ProductCard
                                                    key={
                                                        (bundleConfig.same_addon_for_all_variants
                                                            ? ""
                                                            : currentVariantId + "-") +
                                                        product?.id +
                                                        product?.variants?.[0]?.id
                                                    }
                                                    index={index}
                                                    product={product}
                                                    bundleConfig={bundleConfig}
                                                    appConfigContext={appConfigContext}
                                                    standAlone={!!standAlone}
                                                    setTrackVariant={setTrackVariant}
                                                    isMobile={isMobile}
                                                    setFinalProductsList={setFinalProductsList}
                                                    finalProductsList={finalProductsList}
                                                    shouldDiscountApply={shouldDiscountApply}
                                                    variants={product?.variants}
                                                />
                                            );
                                        })
                                    )
                                ) : (
                                    <ProductCardShimmer
                                        brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi}
                                        hasRequiredProduct={
                                            finalProductsList?.filter((product) => !product.required_product)?.length >
                                            0
                                                ? false
                                                : true
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <ProductCardShimmer
                                brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi}
                                hasRequiredProduct={bundleConfig?.product_add_on_selection == "manual" ? false : true}
                            />
                        )}
                    </>
                )}
            </BundlePreviewWrapper>
        </div>
    );
};

export default ProductAddonsBundle;
